'use client';

/**
 * FeatureAccordion.tsx — funksjoner som akkordion + bildeflate (ibm.com «Features»)
 *
 * Venstre: akkordion der kun ett punkt er åpent (første som standard), med
 * blå venstrekant og halvfet tittel når åpent. Høyre: illustrasjon (`art`).
 * Knapper med aria-expanded/aria-controls; piltaster er ikke nødvendig siden
 * hvert punkt er en vanlig knapp i tab-rekkefølgen.
 */

import { useId, useState, type ReactNode } from 'react';
import { ChevronDown } from '@carbon/icons-react';
import type { ProductFeatures } from '@/types/product';
import SectionHeader from './SectionHeader';
import styles from './product.module.css';

export default function FeatureAccordion({
  features,
  art,
  id = 'funksjoner',
}: {
  features: ProductFeatures;
  art: ReactNode;
  id?: string;
}) {
  const [open, setOpen] = useState(0);
  const uid = useId();

  return (
    <section id={id} className={styles.section} aria-labelledby={`${id}-heading`}>
      <div className={styles.container}>
        <SectionHeader id={`${id}-heading`} eyebrow={features.eyebrow} heading={features.heading}>
          <p className={styles.lead}>{features.intro}</p>
        </SectionHeader>

        <div className={styles.split}>
          <div className={styles.acc}>
            {features.items.map((item, i) => {
              const isOpen = open === i;
              return (
                <div key={item.title} className={`${styles.accItem} ${isOpen ? styles.accOpen : ''}`}>
                  <h3 className={styles.accHeading}>
                    <button
                      type="button"
                      className={styles.accButton}
                      aria-expanded={isOpen}
                      aria-controls={`${uid}-panel-${i}`}
                      id={`${uid}-tab-${i}`}
                      onClick={() => setOpen(isOpen ? -1 : i)}
                    >
                      <span>{item.title}</span>
                      <ChevronDown size={20} aria-hidden className={styles.accChevron} />
                    </button>
                  </h3>
                  <div
                    id={`${uid}-panel-${i}`}
                    role="region"
                    aria-labelledby={`${uid}-tab-${i}`}
                    hidden={!isOpen}
                    className={styles.accPanel}
                  >
                    <p>{item.body}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className={styles.art}>
            {art}
          </div>
        </div>
      </div>
    </section>
  );
}
